import {
    Component,
    Input,
    OnInit,
    OnDestroy,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { HisJobOption } from '../../model/his-job-option';
import { HisJobService } from '../../service/his-job.service';
import { HisJobHandle } from '../../model/his-job-handle';
import { HisJobRequest } from '../../model/his-job-request';
import { HisJobStatus } from '../../model/his-job-status';
import { HisSearchRequest, HisSortCriteria } from '../../../genericsearch/model/his-search-request';
import { HisOption } from '../../../genericsearch/model/his-option';
import { HisAuthService } from '../../../auth/service/his-auth.service';
import { HisNotificationService } from '../../../core/service/notifications/his-notification.service';
import { HisUiService } from '../../../core/service/base/his-ui.service';
import { HisSearch } from '../../../genericsearch/model/search/his-search';
import { HisJobConfigurationComponent } from '../job-config/his-job-configuration.component';
import { HisSearchResultColumnSortHelper } from '../../../genericsearch/model/his-search-result-column-sort-helper';

@Component({
    selector: 'his-job-starter',
    templateUrl: './his-job-starter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class HisJobStarterComponent implements OnInit, OnDestroy {
    private jobService = inject(HisJobService);
    private notificationService = inject(HisNotificationService);
    private uiTextService = inject(HisUiService);

    public static jobRight = 'RIGHT_CS_SYS_JOBS_RIGHT_EXECUTE_JOB';

    @Input() key: string;

    @Input() search: HisSearch;
    @Input() searchConfigFile: string;

    @Input() selectedOptions: HisOption[];

    @Input() showDebug = false;
    @Input() debugAvailable = false;

    @Input() selectJobLabel = '';

    @ViewChild(HisJobConfigurationComponent)
    private jobConfiguration: HisJobConfigurationComponent;

    public selectedJob: HisJobOption;
    public filteredJobs: HisJobOption[];
    public availableJobs: HisJobOption[] = [];
    public jobListSelection: HisJobOption | null = null;
    public showJobListDialog = false;

    public lastStartedJob: HisJobStatus;
    public lastAutoDownloadedURL: string;
    public lastError: any;

    public isAuthorized = false;

    constructor() {
        const authService = inject(HisAuthService);

        this.isAuthorized = authService.hasAll(HisJobStarterComponent.jobRight);
    }

    ngOnInit() {
        if (this.isAuthorized) {
            this.loadJobOptions();
        }
    }

    ngOnDestroy(): void {
        this.lastStartedJob = null;
    }

    loadJobOptions() {
        this.jobService.getAvailableJobs(this.key).subscribe(
            (options: HisJobOption[]) => {
                this.availableJobs = options;
            },
            (error) => {
                console.log(error);
                this.lastError = error;
            },
        );
    }

    filterJobs(event: any) {
        this.filteredJobs = [];
        for (let jobMeta of this.availableJobs) {
            if (
                jobMeta.name
                    .toLowerCase()
                    .indexOf(event.query.toLowerCase()) === 0
            ) {
                this.filteredJobs.push(jobMeta);
            }
        }
    }

    showJobConfigForJob( job: HisJobOption ) {
        this.selectedJob = job;
        if ( this.jobConfiguration ) {
            this.jobConfiguration.showJobConfiguration();
        }
    }

    startJob(job?: HisJobOption) {
        if (job) {
            this.selectedJob = job;
        }

        if ( this.mustShowJobConfiguration() ) {
            this.jobConfiguration.showJobConfiguration();
            return;
        }

        let jobRequest = new HisJobRequest(this.selectedJob);
        jobRequest.jobStarterKey = this.key;

        // Suchdaten mit übertragen, falls vorhanden
        if (this.searchConfigFile) {
            jobRequest.searchRequest = new HisSearchRequest( this.search );
            jobRequest.searchRequest.sortCriteria ||= [];
            jobRequest.searchRequest.sortCriteria.push( ...HisSearchResultColumnSortHelper.getSafedSortCriteria() );
        }

        // Manuelle Suchselektion mit übertragen, falls vorhanden
        if (this.selectedOptions && this.selectedOptions.length > 0) {
            jobRequest.selectedRowData = this.selectedOptions;
        }

        // Parameter aus JobSettings sammeln und mit übertragen
        let jobParams = {};
        this.collectJobParamsFromSetting(
            this.selectedJob.defaultJobSettings,
            jobParams,
            '',
        );
        jobRequest.jobParams = jobParams;

        this.jobService.startJob(jobRequest).subscribe(
            (status: HisJobStatus) => {
                this.lastStartedJob = status;
                let successMsg = this.uiTextService.translate(
                    'cs.sys.jobs.successfully_started',
                    status.jobName,
                );
                let category = this.uiTextService.translate(
                    'messagebox.singleSuccessTitle',
                );
                this.notificationService.info(successMsg, category);
                this.monitorStatus(status.handle, 1);
            },
            (error) => {
                console.log(error);
                this.lastError = error;
            },
        );
    }

    private mustShowJobConfiguration() {
        if ( !this.jobConfiguration ) {
            return false;
        }

        if ( this.selectedJob.forceShowConfig && !this.jobConfiguration.jobConfigurationVisible ) {
            return true;
        }

        return !this.jobConfiguration.areSettingsValid( this.selectedJob.defaultJobSettings );
    }

    collectJobParamsFromSetting(
        settings: any[],
        jobParams: Record<string, any>,
        prefix: string,
    ) {
        for (const setting of settings) {
            if (setting.settings) {
                this.collectJobParamsFromSetting(
                    setting.settings,
                    jobParams,
                    prefix,
                );
                continue;
            }

            if (setting.childSettings) {
                this.collectJobParamsFromSetting(
                    setting.childSettings,
                    jobParams,
                    prefix + '.' + setting.name,
                );
                continue;
            }

            let settingName = prefix + '.' + setting.name;
            if (settingName.startsWith('.')) {
                settingName = settingName.substring(1);
            }
            // #310483 beim ersten Klick auf eines der Schnellstart-Items "Publikationsliste (ODT)" oder "Publikationsliste (PDF)" haben
            // wir hier für das Setting mit name="citationStyle" hier noch keinen selectedValue und value="item-1".
            // In dem Fall müssen wir anscheinend also den defaultValue nehmen.
            if (!(setting.selectedValue == null)) {
                jobParams[settingName] = setting.selectedValue;
                continue;
            }
            if (setting.value !== null || setting.options) {
                switch (setting.type) {
                    case 'SELECT_ONE': {
                        const settingOptionValue = setting?.options?.find(
                            (options: any) => options.key === setting.value,
                        )?.value;
                        if (settingOptionValue !== null && setting.options) {
                            jobParams[settingName] = settingOptionValue;
                        }
                        continue;
                    }
                    case 'SELECT_MANY': {
                        jobParams[settingName] = JSON.stringify(setting.value);
                        continue;
                    }
                    case 'FILE': {
                        const uploadedFiles = setting.value?.map(
                            (file: any) => ({
                                name: file.name,
                                contentType: file.contentType,
                                base64: file.base64,
                            }),
                        );
                        jobParams[settingName] = JSON.stringify(uploadedFiles);
                        continue;
                    }
                    default: {
                        jobParams[settingName] = setting.value;
                        continue;
                    }
                }
            }
            if (setting.defaultValue !== null) {
                jobParams[settingName] = setting.defaultValue;
            }
        }
    }

    monitorStatus(handle: HisJobHandle, iterationCount: number) {
        if (
            this.lastStartedJob &&
            this.lastStartedJob.handle.executionId === handle.executionId &&
            !this.lastStartedJob.finishedAt
        ) {
            setTimeout(() => {
                this.jobService.getJobStatus(handle).subscribe(
                    (status: HisJobStatus) => {
                        if (
                            this.lastStartedJob &&
                            this.lastStartedJob.handle.executionId ===
                                handle.executionId
                        ) {
                            this.lastStartedJob = status;
                            this.downloadResultIfPresent();
                            this.monitorStatus(handle, iterationCount + 1);
                        }
                    },
                    (error) => {
                        console.log(error);
                        this.lastError = error;
                    },
                );
            }, this.getDelayForIteration(iterationCount));
        }
    }

    /*
     * Am Anfang häufiger auf den Status zugreifen, damit Ergebnisse schneller Jobs zeitnah angeboten werden.
     * Bei lange dauernden Jobs kommt es nicht mehr so drauf an - dann länger warten um den Server zu schonen.
     */
    getDelayForIteration(iterationCount: number): number {
        if (iterationCount < 5) {
            return iterationCount * 200;
        } else if (iterationCount < 10) {
            return iterationCount * 400;
        }

        return 5000;
    }

    downloadResultIfPresent() {
        if (
            this.lastStartedJob.resultDocumentUrl &&
            !(
                this.lastStartedJob.resultDocumentUrl ===
                this.lastAutoDownloadedURL
            )
        ) {
            this.lastAutoDownloadedURL = this.lastStartedJob.resultDocumentUrl;
            window.location.href = this.lastStartedJob.resultDocumentUrl;
        }
    }

    toggleShowDebug() {
        this.showDebug = !this.showDebug;
    }
}
